export type DataDelta = {
    days: number;
    hours: number;
    minutes: number;
};

/**
 * Calculates the delta in the number of days and detailed time difference (days, hours, minutes)
 * between the given date and the current date.
 *
 * @param {Date} inputDate The date to compare with the current date. e.g. 10/01/2024 00:00:00
 * @returns An object containing the difference in days and detailed time difference.
 *
 * @example
 * Assuming today's date is October 5, 2023, the following would return { dayDiff: 2, dayTimeDiff: { days: 2, hours: 0, minutes: 0 } }
 * dateCountdown(new Date(2023, 9, 3)); // 9 represents October because months are 0-indexed
 */
export const dateCountdown = (inputDate: Date): DataDelta | null => {
    if (!(inputDate instanceof Date)) {
        throw new Error('Argument must be a Date object');
    }

    const currentDate = new Date();

    // Get the total difference in milliseconds
    const totalMilliseconds = inputDate.getTime() - currentDate.getTime();

    if (totalMilliseconds < 59999) {
        // If the input date is in the past or , return less than 60 seconds return null
        return null;
    }

    // Calculate the number of days
    const days = Math.floor(totalMilliseconds / (1000 * 60 * 60 * 24));
    const remainingAfterDays = totalMilliseconds % (1000 * 60 * 60 * 24);

    // Calculate the number of hours
    const hours = Math.floor(remainingAfterDays / (1000 * 60 * 60));
    const remainingAfterHours = remainingAfterDays % (1000 * 60 * 60);

    // Calculate the number of minutes
    const minutes = Math.floor(remainingAfterHours / (1000 * 60));

    return {
        days,
        hours,
        minutes,
    };
};

export const countdownStringFormat = (date: DataDelta): string => {
    return Object.entries(date).reduce((acc, [string, value]) => {
        if (!value) return acc;
        const formatTime = `${value.toString().padStart(2, '0')} ${string}`;
        return acc.length ? `${acc} : ${formatTime}` : formatTime;
    }, '');
};

export const daysDifference = (date1: Date, date2: Date): string => {
    const diffTime = Math.abs(date2.getTime() - date1.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays.toString().padStart(2, '0')
  }
  